// ═══════════════════════════════════════════════════════════════════
// pdf-utils.js — Générateurs PDF (jsPDF + AutoTable)
// Fonctionne via window.jspdf (chargé en vendor global dans index.html)
// Dépend de : utils.js, constants.js (CTRL_PIECES, CTRL_CHECKS)
// ═══════════════════════════════════════════════════════════════════
import { today, fd, fdur, fname, getBreaks, totalBreakMs, totalWorkMs, pointageTache, ecartsJournee } from './utils.js'
import { CTRL_PIECES, CTRL_CHECKS } from './constants.js'
import { getRapportStructure } from './components/ErrorBoundary.jsx'

// ─── Helper local : totalWorkMs adapté pour la période (évite conflit de nom) ───
export const totalWorkMs2 = c => {
  if (!c?.sw?.ts) return 0
  const end = c.ew?.ts ? new Date(c.ew.ts) : new Date()
  const breakMs = totalBreakMs(c)
  return Math.max(0, end - new Date(c.sw.ts) - breakMs)
}

// ─── Helper interne : convertit une URL distante en dataURL pour jsPDF ───
async function urlToDataUrlForPdf(src) {
  if (!src) return src
  if (typeof src === 'string' && src.startsWith('data:')) return src
  try {
    const r = await fetch(src)
    if (!r.ok) throw new Error('HTTP ' + r.status)
    const b = await r.blob()
    return await new Promise((res, rej) => {
      const reader = new FileReader()
      reader.onload = () => res(reader.result)
      reader.onerror = () => rej(new Error('FileReader failed'))
      reader.readAsDataURL(b)
    })
  } catch (e) {
    console.error('[urlToDataUrlForPdf] failed for', src, e)
    return null
  }
}

// ─── Génération PDF récapitulatif journalier d'un salarié ───
export const generateHistoryPdf = ({ worker, c, dayAls, biens, date }) => {
  if (!window.jspdf) { alert('Bibliothèque PDF non chargée. Rafraîchissez la page.'); return; }
  const { jsPDF } = window.jspdf
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageW = 210
  const margin = 15
  const dateStr = new Date(date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

  const fmtMs = ms => ms ? fdur(ms) : '—'
  const fmtH = ts => ts ? new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'

  doc.setFillColor(45, 107, 62)
  doc.rect(0, 0, pageW, 28, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text('LA CONCIERGERIE', margin, 13)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.text('Récapitulatif individuel de pointage', margin, 19)
  doc.text('Document interne — usage RH', margin, 24)

  doc.setFillColor(255, 255, 255)
  doc.setDrawColor(255, 255, 255)
  doc.roundedRect(pageW - margin - 72, 5, 72, 18, 2, 2, 'F')
  doc.setTextColor(45, 107, 62)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text(fname(worker) || '—', pageW - margin - 68, 11)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(80, 80, 80)
  doc.text((worker._gerant ? 'Direction' : worker.role || 'Salarié'), pageW - margin - 68, 16)
  doc.text(dateStr, pageW - margin - 68, 20)

  let y = 38

  doc.setTextColor(45, 107, 62)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.text('Récapitulatif de la journée', margin, y)
  y += 2
  doc.setDrawColor(45, 107, 62)
  doc.setLineWidth(0.5)
  doc.line(margin, y, pageW - margin, y)
  y += 6

  const wMs = totalWorkMs(c)
  const bMs = totalBreakMs(c)
  let intMs = 0
  dayAls.filter(l => l.type === 'end').forEach(endLog => {
    const startLog = dayAls.find(x => x.tid === endLog.tid && x.type === 'start')
    if (startLog) intMs += Math.max(0, new Date(endLog.ts) - new Date(startLog.ts))
  })

  doc.autoTable({
    startY: y,
    head: [['Début de poste', 'Fin de poste', 'Cumul pauses', 'Travail effectif', 'Total interv. appart.']],
    body: [[
      c?.sw ? fmtH(c.sw.ts) + (c.sw._admin ? ' ✎' : '') : '—',
      c?.ew ? fmtH(c.ew.ts) + (c.ew._admin ? ' ✎' : '') : '—',
      bMs > 0 ? fmtMs(bMs) : '—',
      fmtMs(wMs),
      fmtMs(intMs),
    ]],
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 3, halign: 'center' },
    headStyles: { fillColor: [45, 107, 62], textColor: 255, fontStyle: 'bold', fontSize: 9 },
    margin: { left: margin, right: margin },
  })
  y = doc.lastAutoTable.finalY + 8

  // ─── Résumé des écarts (v164) ───
  // Décomposition factuelle intervention / pause déclarée / hors intervention, même fonction
  // pure que la modale d'analyse (SecRH). Affichage neutre, sans seuil ni signalement
  // automatique (décision explicite de Claire, session du 17/08/2026) — absente si la
  // journée n'est pas close (ecartsJournee renvoie null) ou si aucun segment "hors
  // intervention" n'est trouvé (zéro régression visuelle sur une journée sans écart).
  const ecarts = ecartsJournee(c, dayAls, biens)
  const ecartSegs = ecarts ? ecarts.filter(s => s.type === 'ecart') : []
  if (ecartSegs.length > 0) {
    doc.setTextColor(45, 107, 62)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.text('Résumé des écarts (hors intervention)', margin, y)
    y += 2
    doc.line(margin, y, pageW - margin, y)
    y += 4
    doc.autoTable({
      startY: y,
      head: [['Créneau', 'Repère', 'Durée']],
      body: ecartSegs.map(s => [`${fmtH(s.start)} -> ${fmtH(s.end)}`, (s.repere || '').replace(/→/g, '->'), fmtMs(s.ms)]),
      foot: [['Total hors intervention', '', fmtMs(ecartSegs.reduce((s, x) => s + x.ms, 0))]],
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2.5, halign: 'center' },
      headStyles: { fillColor: [100, 100, 100], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      footStyles: { fillColor: [230, 230, 230], textColor: [40, 40, 40], fontStyle: 'bold', fontSize: 9 },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 8
  }

  const breaks = getBreaks(c)
  if (breaks.length > 0) {
    doc.setTextColor(45, 107, 62)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.text('Pauses pointées', margin, y)
    y += 2
    doc.line(margin, y, pageW - margin, y)
    y += 4
    doc.autoTable({
      startY: y,
      head: [['#', 'Début', 'Fin', 'Durée', 'Saisie']],
      body: breaks.map((b, i) => {
        const dms = b.end ? new Date(b.end) - new Date(b.start) : null
        return [String(i + 1), fmtH(b.start), b.end ? fmtH(b.end) : '(en cours)', dms ? fmtMs(dms) : '—', b._admin ? 'Admin' : 'Salarié']
      }),
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2.5, halign: 'center' },
      headStyles: { fillColor: [122, 90, 0], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 8
  }

  const sortedAls = [...dayAls].sort((a, b) => a.ts.localeCompare(b.ts))
  const events = []
  if (c?.sw) events.push({ type: 'work_start', kind: 'start', ts: c.sw.ts, label: 'Début de poste', admin: c.sw._admin })
  breaks.forEach((b, i) => {
    events.push({ type: 'break_start', kind: 'end', ts: b.start, label: `Début pause #${i + 1}`, admin: b._admin })
    if (b.end) events.push({ type: 'break_end', kind: 'start', ts: b.end, label: `Fin pause #${i + 1}`, admin: b._admin })
  })
  sortedAls.forEach(l => {
    const b = biens.find(x => x.id === l.bid)
    events.push({ type: 'apt', kind: l.type === 'start' ? 'start' : 'end', ts: l.ts, label: `${l.type === 'start' ? 'Début' : 'Fin'} : ${b?.name || '?'}`, geo: l, admin: l.editedBy })
  })
  if (c?.ew) events.push({ type: 'work_end', kind: 'end', ts: c.ew.ts, label: 'Fin de poste', admin: c.ew._admin })
  events.sort((a, b) => a.ts.localeCompare(b.ts))

  if (events.length > 1) {
    doc.setTextColor(45, 107, 62)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.text('Chronologie détaillée', margin, y)
    y += 2
    doc.line(margin, y, pageW - margin, y)
    y += 4
    let prevEvent = null
    const rows = events.map(ev => {
      const showGap = prevEvent && (prevEvent.kind === 'end' || (prevEvent.type === 'work_start' && ev.kind === 'start'))
      const gap = showGap ? fmtMs(new Date(ev.ts) - new Date(prevEvent.ts)) : ''
      const heure = fmtH(ev.ts)
      let geoCell = ''
      if (ev.geo) {
        if (ev.geo.ok === true) geoCell = `OK (${ev.geo.dist}m)`
        else if (ev.geo.ok === false) geoCell = `Hors zone ${ev.geo.dist}m`
        else if (ev.geo.geoErr) geoCell = 'Refusée'
        else geoCell = '—'
      }
      const tag = ev.admin ? 'Admin' : ''
      prevEvent = ev
      return [heure, ev.label, gap, geoCell, tag]
    })
    doc.autoTable({
      startY: y,
      head: [['Heure', 'Événement', 'Écart', 'Géoloc.', 'Saisie']],
      body: rows,
      theme: 'striped',
      styles: { fontSize: 9, cellPadding: 2.5 },
      headStyles: { fillColor: [26, 84, 144], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'center', cellWidth: 18 }, 2: { halign: 'center', cellWidth: 22 }, 3: { halign: 'center', cellWidth: 30 }, 4: { halign: 'center', cellWidth: 18 } },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 8
  }

  const byTid = {}
  sortedAls.forEach(l => { if (!byTid[l.tid]) byTid[l.tid] = { tid: l.tid, bid: l.bid }; byTid[l.tid][l.type] = l })
  const aptRows = Object.values(byTid).map(g => {
    const b = biens.find(x => x.id === g.bid)
    const dms = g.start && g.end ? new Date(g.end.ts) - new Date(g.start.ts) : null
    return [b?.name || '?', g.start ? fmtH(g.start.ts) : '—', g.end ? fmtH(g.end.ts) : '—', dms ? fmtMs(dms) : (g.start ? '(en cours)' : '—')]
  })
  if (aptRows.length > 0) {
    if (y > 240) { doc.addPage(); y = 20 }
    doc.setTextColor(45, 107, 62)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.text('Temps par appartement', margin, y)
    y += 2
    doc.line(margin, y, pageW - margin, y)
    y += 4
    doc.autoTable({
      startY: y,
      head: [['Appartement', 'Début', 'Fin', 'Durée']],
      body: aptRows,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2.5, halign: 'center' },
      headStyles: { fillColor: [45, 107, 62], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 8
  }

  const reminderHeight = 32
  if (y + reminderHeight > 280) { doc.addPage(); y = 20 }
  const pageH = 297
  y = Math.max(y, pageH - reminderHeight - 15)
  doc.setFillColor(255, 244, 214)
  doc.setDrawColor(229, 201, 119)
  doc.setLineWidth(0.4)
  doc.roundedRect(margin, y, pageW - 2 * margin, reminderHeight, 2, 2, 'FD')
  doc.setTextColor(122, 90, 0)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9.5)
  doc.text('Importance du pointage rigoureux', margin + 4, y + 5.5)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  doc.setTextColor(60, 40, 0)
  const reminderLines = [
    "Les pointages servent à l'établissement de votre paie, à la facturation des clients, et à garantir votre sécurité",
    'en tant que travailleur isolé (vérification de votre localisation à chaque intervention). Tout refus ou pointage',
    'non conforme pourra entraîner des sanctions disciplinaires. En cas de difficulté technique pour pointer, la',
    "direction se tient à votre disposition via le module Messagerie de l'application.",
  ]
  reminderLines.forEach((line, i) => { doc.text(line, margin + 4, y + 10 + i * 4) })

  doc.setFontSize(7)
  doc.setTextColor(120, 120, 120)
  doc.text(`Édité le ${new Date().toLocaleString('fr-FR')} par La Conciergerie · ${fname(worker)} · ${dateStr}`, margin, pageH - 7)

  const safeName = (fname(worker) || 'salarie').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '_')
  const safeDate = date.replace(/-/g, '')
  doc.save(`pointage_${safeName}_${safeDate}.pdf`)
}

// ─── Génération PDF récapitulatif des pointages sur une période (multi-journées) ───
export const generatePeriodePdf = ({ filtered, als, biens, allWorkers, histSid, histDeb, histFin, findWorker }) => {
  if (!window.jspdf) { alert('Bibliothèque PDF non chargée. Rafraîchissez la page.'); return; }
  const { jsPDF } = window.jspdf
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageW = 210; const margin = 14
  const GREEN = [45, 107, 62]; const GREY = [100, 100, 100]; const LIGHTGREY = [245, 245, 245]
  const fmtH = ts => ts ? new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'
  const worker = histSid ? findWorker(histSid) : null
  const periodeLabel = (histDeb || histFin)
    ? ((histDeb ? new Date(histDeb).toLocaleDateString('fr-FR') : 'debut') + ' au ' + (histFin ? new Date(histFin).toLocaleDateString('fr-FR') : 'fin'))
    : 'Toutes dates'

  doc.setFillColor(...GREEN)
  doc.rect(0, 0, pageW, 30, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  doc.text('LA CONCIERGERIE', margin, 14)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text('Recapitulatif des pointages', margin, 21)
  doc.setFontSize(9)
  doc.text('Periode : ' + periodeLabel, margin, 27)

  doc.setFillColor(255, 255, 255)
  doc.setDrawColor(255, 255, 255)
  doc.roundedRect(pageW - margin - 70, 5, 70, 20, 2, 2, 'F')
  doc.setTextColor(...GREEN)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  const nomLabel = worker ? fname(worker) : 'Tous les salaries'
  doc.text(nomLabel, pageW - margin - 66, 13, { maxWidth: 62 })
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(...GREY)
  if (worker) doc.text(worker._gerant ? 'Direction' : worker.role || 'Salarie', pageW - margin - 66, 18)
  doc.text('Edite le ' + new Date().toLocaleDateString('fr-FR'), pageW - margin - 66, 23)

  let y = 38

  let totWMs = 0, totIntMs = 0, totBMs = 0
  filtered.forEach(c => {
    totWMs += totalWorkMs2(c)
    totBMs += totalBreakMs(c)
    const dayAls = als.filter(l => l.sid === c.sid && l.ts.slice(0, 10) === c.date)
    dayAls.filter(l => l.type === 'end').forEach(e => {
      const s = dayAls.find(x => x.tid === e.tid && x.type === 'start')
      if (s) totIntMs += Math.max(0, new Date(e.ts) - new Date(s.ts))
    })
  })

  doc.setTextColor(...GREEN)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text('Resume de la periode', margin, y)
  y += 2
  doc.setDrawColor(...GREEN)
  doc.setLineWidth(0.4)
  doc.line(margin, y, pageW - margin, y)
  y += 5
  doc.autoTable({
    startY: y,
    head: [['Journees', 'Travail effectif total', 'Pauses totales', 'Interventions appartements']],
    body: [[
      String(filtered.length),
      fdur(totWMs) || '0h00',
      totBMs > 0 ? fdur(totBMs) : '0h00',
      totIntMs > 0 ? fdur(totIntMs) : '0h00',
    ]],
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 4, halign: 'center' },
    headStyles: { fillColor: GREEN, textColor: 255, fontStyle: 'bold', fontSize: 9 },
    margin: { left: margin, right: margin },
  })
  y = doc.lastAutoTable.finalY + 10

  doc.setTextColor(...GREEN)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text('Detail par journee', margin, y)
  y += 2
  doc.line(margin, y, pageW - margin, y)
  y += 4

  const rows = filtered.map(c => {
    const w = findWorker(c.sid)
    const wMs2 = totalWorkMs2(c)
    const bMs = totalBreakMs(c)
    const dayAls = als.filter(l => l.sid === c.sid && l.ts.slice(0, 10) === c.date)
    let intMs = 0
    dayAls.filter(l => l.type === 'end').forEach(e => {
      const s = dayAls.find(x => x.tid === e.tid && x.type === 'start')
      if (s) intMs += Math.max(0, new Date(e.ts) - new Date(s.ts))
    })
    const d = new Date(c.date)
    return [
      d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }),
      w ? fname(w) + (w._gerant ? ' (dir.)' : '') : '—',
      c.sw ? fmtH(c.sw.ts) + (c.sw._admin ? ' *' : '') : '—',
      c.ew ? fmtH(c.ew.ts) + (c.ew._admin ? ' *' : '') : '—',
      bMs > 0 ? fdur(bMs) : '—',
      wMs2 ? fdur(wMs2) : '—',
      intMs > 0 ? fdur(intMs) : '—',
    ]
  })

  doc.autoTable({
    startY: y,
    head: [['Date', 'Salarie', 'Debut', 'Fin', 'Pauses', 'Effectif', 'Interv.']],
    body: rows,
    theme: 'striped',
    styles: { fontSize: 8.5, cellPadding: 3 },
    headStyles: { fillColor: GREEN, textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
    alternateRowStyles: { fillColor: LIGHTGREY },
    columnStyles: {
      0: { cellWidth: 36 },
      1: { cellWidth: 42 },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 18, halign: 'center' },
      4: { cellWidth: 18, halign: 'center' },
      5: { cellWidth: 20, halign: 'center', fontStyle: 'bold' },
      6: { cellWidth: 20, halign: 'center' },
    },
    margin: { left: margin, right: margin },
    didDrawPage: () => {
      doc.setFontSize(7); doc.setTextColor(...GREY)
      doc.text('La Conciergerie — Document interne RH — Confidentiel', margin, 295)
      const pg = doc.internal.getCurrentPageInfo().pageNumber
      doc.text('Page ' + pg, pageW - margin - 12, 295)
    },
  })

  const nomWorker = worker ? fname(worker).replace(/\s+/g, '-') : 'tous'
  const deb = histDeb || 'debut'; const fin = histFin || 'fin'
  doc.save('pointages_' + nomWorker + '_' + deb + '_' + fin + '.pdf')
}

// ─── Génération PDF du journal Q/R « pause oubliée » (Lot D, encadrement renforcé) ───
// Liste la question posée (trou détecté / trajet estimé / excédent) et la réponse du
// salarié (Oui/Non) pour chaque ligne sélectionnée. Même gabarit que generatePeriodePdf
// (tableau simple, sans bloc signature) — cohérent avec l'export PDF des heures.
export const generatePauseQRPdf = ({ rows, findWorker }) => {
  if (!window.jspdf) { alert('Bibliothèque PDF non chargée. Rafraîchissez la page.'); return; }
  const { jsPDF } = window.jspdf
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageW = 210; const margin = 14
  const GREEN = [45, 107, 62]; const GREY = [100, 100, 100]; const LIGHTGREY = [245, 245, 245]

  doc.setFillColor(...GREEN)
  doc.rect(0, 0, pageW, 30, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  doc.text('LA CONCIERGERIE', margin, 14)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text('Journal des pauses suggerees (trous entre appartements)', margin, 21)
  doc.setFontSize(9)
  doc.text('Edite le ' + new Date().toLocaleDateString('fr-FR'), margin, 27)

  let y = 38
  const rowsSorted = [...(rows || [])].sort((a, b) => (a.ts || '').localeCompare(b.ts || ''))
  const body = rowsSorted.map(q => {
    const w = findWorker ? findWorker(q.sid) : null
    return [
      q.date ? new Date(q.date).toLocaleDateString('fr-FR') : '—',
      w ? fname(w) + (w._gerant ? ' (dir.)' : '') : '—',
      q.ts ? new Date(q.ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—',
      `${q.prevBienName || '?'} -> ${q.newBienName || '?'}`,
      q.gapMin != null ? q.gapMin + ' min' : '—',
      q.expected != null ? '~' + q.expected + ' min' : '—',
      q.excess != null ? q.excess + ' min' : '—',
      q.reponse === 'oui' ? 'Oui, pause' : 'Non, pas de pause',
    ]
  })

  doc.autoTable({
    startY: y,
    head: [['Date', 'Salarie', 'Heure', 'Trajet', 'Trou reel', 'Trajet estime', 'Excedent', 'Reponse']],
    body,
    theme: 'striped',
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: GREEN, textColor: 255, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: LIGHTGREY },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: 26 },
      2: { cellWidth: 15, halign: 'center' },
      3: { cellWidth: 38 },
      4: { cellWidth: 18, halign: 'center' },
      5: { cellWidth: 24, halign: 'center' },
      6: { cellWidth: 20, halign: 'center' },
      7: { cellWidth: 19, halign: 'center', fontStyle: 'bold' },
    },
    margin: { left: margin, right: margin },
    didDrawPage: () => {
      doc.setFontSize(7); doc.setTextColor(...GREY)
      doc.text('La Conciergerie — Document interne RH — Confidentiel', margin, 295)
      const pg = doc.internal.getCurrentPageInfo().pageNumber
      doc.text('Page ' + pg, pageW - margin - 12, 295)
    },
  })

  doc.save('pauses_suggerees_' + new Date().toISOString().slice(0, 10).replace(/-/g, '') + '.pdf')
}

// ─── Génération PDF des signalements de seuil qualité (dureeAnom7, v154 révisé 11/08/2026) ───
// Même gabarit que generatePauseQRPdf, complété le 14/08/2026 à la demande de Claire :
// durée réelle + heures du ménage (dérivées de `pointageTache(a.tid,als)`, même fonction
// pure que le PDF contrôle depuis v150 — dureeAnom7 ne stocke pas la durée, seulement
// `tid`, donc aucune migration nécessaire) et une ligne vierge sous chaque signalement
// pour annotation manuscrite. `als` est désormais un paramètre requis pour la durée —
// optionnel quand même (défaut []) pour ne jamais casser un appel existant : sans
// pointage retrouvé, les colonnes affichent simplement '—' comme ailleurs dans l'appli.
// Thème `grid` (bordures visibles, pas de zébrage) : plus lisible pour écrire à la main
// dans la ligne commentaire que le zébrage `striped` d'origine.
export const generateDureeAnomPdf = ({ rows, findWorker, als }) => {
  if (!window.jspdf) { alert('Bibliothèque PDF non chargée. Rafraîchissez la page.'); return; }
  const { jsPDF } = window.jspdf
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageW = 210; const margin = 14
  const GREEN = [45, 107, 62]; const GREY = [100, 100, 100]; const LIGHTGREY = [248, 248, 248]

  doc.setFillColor(...GREEN)
  doc.rect(0, 0, pageW, 30, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  doc.text('LA CONCIERGERIE', margin, 14)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text('Signalements de seuil qualité (durée min/max)', margin, 21)
  doc.setFontSize(9)
  doc.text('Edite le ' + new Date().toLocaleDateString('fr-FR'), margin, 27)

  let y = 38
  const rowsSorted = [...(rows || [])].sort((a, b) => (b.ts || '').localeCompare(a.ts || ''))
  const COLSPAN = 10
  const body = []
  rowsSorted.forEach(a => {
    const w = findWorker ? findWorker(a.sid) : null
    const { startTs, endTs, ms } = pointageTache(a.tid, als || [])
    const heureDebut = startTs ? new Date(startTs).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'
    const heureFin = endTs ? new Date(endTs).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'
    const dureeReelle = ms !== null ? fdur(ms) : '—'
    const motifAffiche = (a.motif || '—') + ((a.sens === 'sous' && a.qualiteConfirmee) ? ' [Qualité confirmée]' : '')
    body.push([
      a.ts ? new Date(a.ts).toLocaleDateString('fr-FR') : '—',
      a.ts ? new Date(a.ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—',
      w ? fname(w) + (w._gerant ? ' (dir.)' : '') : (a.fromName || '—'),
      a.bienName || '—',
      a.sens === 'sous' ? 'Trop rapide' : 'Trop long',
      heureDebut,
      heureFin,
      dureeReelle,
      motifAffiche,
      a.traite ? 'Traité' + (a.traitePar ? ' (' + a.traitePar + ')' : '') : 'En attente',
    ])
    // Ligne vierge pour annotation manuscrite — colSpan sur toute la largeur du tableau,
    // hauteur minimale forcée pour laisser assez de place à l'écriture.
    body.push([{ content: 'Commentaire :', colSpan: COLSPAN, styles: { fontStyle: 'italic', textColor: GREY, minCellHeight: 9, valign: 'middle', fillColor: LIGHTGREY } }])
  })

  doc.autoTable({
    startY: y,
    head: [['Date', 'Heure', 'Salarié', 'Bien', 'Sens', 'Ménage de', 'à', 'Durée', 'Motif', 'Statut']],
    body,
    theme: 'grid',
    styles: { fontSize: 7.3, cellPadding: 2.2 },
    headStyles: { fillColor: GREEN, textColor: 255, fontStyle: 'bold', fontSize: 7.3 },
    columnStyles: {
      0: { cellWidth: 17 },
      1: { cellWidth: 12, halign: 'center' },
      2: { cellWidth: 21 },
      3: { cellWidth: 20 },
      4: { cellWidth: 15, halign: 'center' },
      5: { cellWidth: 12, halign: 'center' },
      6: { cellWidth: 12, halign: 'center' },
      7: { cellWidth: 14, halign: 'center' },
      8: { cellWidth: 39 },
      9: { cellWidth: 20 },
    },
    margin: { left: margin, right: margin },
    didDrawPage: () => {
      doc.setFontSize(7); doc.setTextColor(...GREY)
      doc.text('La Conciergerie — Document interne RH — Confidentiel', margin, 295)
      const pg = doc.internal.getCurrentPageInfo().pageNumber
      doc.text('Page ' + pg, pageW - margin - 12, 295)
    },
  })

  doc.save('alertes_duree_' + new Date().toISOString().slice(0, 10).replace(/-/g, '') + '.pdf')
}

// ─── Génération PDF de l'accusé RGPD salarié (SPEC_rgpd_consentement_v1.md) ───
// Texte intégral accepté + identité + horodatage précis + version du document — la
// preuve doit rester lisible même si `rgpdDoc7` évolue plus tard vers une v2 (le texte
// est imprimé tel quel, pas recalculé depuis la version courante). Pas de tableau ici
// (contenu = paragraphe long) : `splitTextToSize` + pagination manuelle, contrairement
// aux autres générateurs de ce fichier qui utilisent autoTable.
export const generateRgpdAckPdf = ({ ack, doc: rgpdDoc, workerName }) => {
  if (!window.jspdf) { alert('Bibliothèque PDF non chargée. Rafraîchissez la page.'); return; }
  const { jsPDF } = window.jspdf
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageW = 210, pageH = 297, margin = 16
  const GREEN = [45, 107, 62]; const GREY = [100, 100, 100]

  const footerAndPage = () => {
    doc.setFontSize(7); doc.setTextColor(...GREY)
    doc.text('La Conciergerie — Document interne RH — Confidentiel', margin, pageH - 8)
    const pg = doc.internal.getCurrentPageInfo().pageNumber
    doc.text('Page ' + pg, pageW - margin - 12, pageH - 8)
  }

  doc.setFillColor(...GREEN)
  doc.rect(0, 0, pageW, 30, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text('LA CONCIERGERIE', margin, 13)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text("Accuse de prise de connaissance - Information RGPD", margin, 20)
  doc.setFontSize(8.5)
  doc.text('Version du document : ' + (rgpdDoc?.version || '—') + '  ·  Langue validee : ' + ((ack?.langue || 'fr').toUpperCase()), margin, 26)

  let y = 40
  doc.setFontSize(9.5)
  doc.setTextColor(40, 40, 40)
  doc.setFont('helvetica', 'bold')
  doc.text('Salarie(e) : ' + (workerName || '—'), margin, y)
  y += 6
  doc.text('Valide le : ' + (ack?.ts ? new Date(ack.ts).toLocaleString('fr-FR') : '—'), margin, y)
  y += 10

  doc.setDrawColor(220, 220, 220)
  doc.line(margin, y, pageW - margin, y)
  y += 8

  const texteSource = (ack?.langue === 'es' ? rgpdDoc?.texte_es : rgpdDoc?.texte_fr) || rgpdDoc?.texte_fr || ''
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(30, 30, 30)
  const maxWidth = pageW - 2 * margin
  const paragraphs = texteSource.split('\n')
  paragraphs.forEach(para => {
    if (para.trim() === '') { y += 4; return }
    const lines = doc.splitTextToSize(para, maxWidth)
    lines.forEach(line => {
      if (y > pageH - 24) { footerAndPage(); doc.addPage(); y = 20 }
      doc.text(line, margin, y)
      y += 4.6
    })
    y += 2
  })

  y += 4
  if (y > pageH - 34) { footerAndPage(); doc.addPage(); y = 20 }
  doc.setFillColor(232, 247, 238)
  doc.setDrawColor(45, 107, 62)
  doc.roundedRect(margin, y, maxWidth, 22, 2, 2, 'FD')
  doc.setFont('helvetica', 'italic')
  doc.setFontSize(8.5)
  doc.setTextColor(45, 107, 62)
  const checkboxSource = (ack?.langue === 'es' ? rgpdDoc?.checkbox_es : rgpdDoc?.checkbox_fr) || rgpdDoc?.checkbox_fr || ''
  const ackLines = doc.splitTextToSize('[X] ' + checkboxSource, maxWidth - 8)
  ackLines.slice(0, 3).forEach((line, i) => doc.text(line, margin + 4, y + 6 + i * 4.6))

  footerAndPage()
  const dateSuffix = ack?.ts ? new Date(ack.ts).toISOString().slice(0, 10).replace(/-/g, '') : new Date().toISOString().slice(0, 10).replace(/-/g, '')
  doc.save('accuse_rgpd_' + (workerName || 'salarie').replace(/\s+/g, '_') + '_' + dateSuffix + '.pdf')
}

// ─── Génération PDF récapitulatif des modifications admin pour signature ───
export const generateAdminEditsPdf = ({ worker, records, biens, editorName }) => {
  if (!window.jspdf) { alert('Bibliothèque PDF non chargée. Rafraîchissez la page.'); return; }
  const { jsPDF } = window.jspdf
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageW = 210, pageH = 297, margin = 15
  const fmtH = ts => ts ? new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'
  const fmtDT = ts => ts ? new Date(ts).toLocaleString('fr-FR') : '—'

  doc.setFillColor(184, 93, 16)
  doc.rect(0, 0, pageW, 28, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text('LA CONCIERGERIE', margin, 13)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.text('Récapitulatif des modifications administratives de pointage', margin, 19)
  doc.text('Document à faire signer par le salarié', margin, 24)

  let y = 38
  doc.setTextColor(0, 0, 0)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.text(`Salarié : ${fname(worker) || '—'}`, margin, y)
  y += 6
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text(`Édité par : ${editorName || 'Admin'} — le ${new Date().toLocaleString('fr-FR')}`, margin, y)
  y += 10

  doc.setFontSize(9)
  const introLines = [
    "Le présent document récapitule les modifications apportées par l'administration aux pointages",
    'du salarié. Ces modifications ont été effectuées en présence et avec l\'accord du salarié, ou suite',
    'à une difficulté technique ayant empêché le bon enregistrement par le salarié lui-même.',
    'Le salarié atteste avoir pris connaissance de ces modifications par sa signature en bas de page.',
  ]
  introLines.forEach((line, i) => { doc.text(line, margin, y + i * 4.5) })
  y += introLines.length * 4.5 + 6

  if (records.length === 0) {
    doc.setFontSize(10)
    doc.setTextColor(120, 120, 120)
    doc.text('Aucune modification à afficher.', margin, y)
    y += 8
  } else {
    const rows = records.map(r => {
      let evtLabel = '', newVal = '—'
      if (r._kind === 'tc') {
        evtLabel = `Pointage journalier du ${new Date(r.date).toLocaleDateString('fr-FR')}`
        const parts = []
        if (r.sw) parts.push(`Début ${fmtH(r.sw.ts)}`)
        if (r.ew) parts.push(`Fin ${fmtH(r.ew.ts)}`)
        const bs = getBreaks(r)
        if (bs.length > 0) parts.push(`${bs.length} pause(s)`)
        newVal = parts.join(' · ') || '—'
      } else if (r._kind === 'als') {
        const b = biens.find(x => x.id === r.bid)
        evtLabel = `${r.type === 'start' ? 'Début' : 'Fin'} appartement : ${b?.name || '?'} (${new Date(r.ts).toLocaleDateString('fr-FR')})`
        newVal = fmtH(r.ts)
      }
      return [
        fmtDT(r.editedAt),
        evtLabel,
        r._adminCreated ? 'Ajout' : 'Modification',
        newVal,
        r.editComment || '(aucun)',
      ]
    })
    doc.autoTable({
      startY: y,
      head: [['Date modif.', 'Événement', 'Type', 'Valeur', 'Justification']],
      body: rows,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2.5, valign: 'top' },
      headStyles: { fillColor: [184, 93, 16], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: 30 }, 2: { cellWidth: 20, halign: 'center' }, 3: { cellWidth: 30, halign: 'center' }, 4: { cellWidth: 'auto' } },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 10
  }

  const sigBlockHeight = 78
  if (y > pageH - sigBlockHeight - 15) doc.addPage()
  y = Math.max(y, pageH - sigBlockHeight - 15)

  doc.setDrawColor(180, 180, 180)
  doc.setLineWidth(0.3)
  doc.setTextColor(0, 0, 0)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.text('Accord et signature du salarié', margin, y)
  y += 5
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8.5)
  const sigLines = [
    'Je soussigné(e), ' + (fname(worker) || '…………………………………………') + ', reconnais avoir pris',
    'connaissance des modifications de pointage listées ci-dessus, en accepte le contenu et',
    'atteste de leur exactitude.',
  ]
  sigLines.forEach((line, i) => { doc.text(line, margin, y + i * 4) })
  y += sigLines.length * 4 + 4

  doc.setFontSize(9)
  doc.text('Fait à : ......................................................', margin, y)
  doc.text('Le : ......./......./.......', pageW / 2 + 5, y)
  y += 8
  doc.text('Signature du salarié :', margin, y)
  doc.text('Signature de la direction :', pageW / 2 + 5, y)
  y += 3
  doc.rect(margin, y, 75, 22)
  doc.rect(pageW / 2 + 5, y, 75, 22)
  doc.setFontSize(7)
  doc.setTextColor(150, 150, 150)
  doc.text('(précédée de la mention « lu et approuvé »)', margin + 1, y + 25)

  doc.setFontSize(7)
  doc.setTextColor(120, 120, 120)
  doc.text(`Édité le ${new Date().toLocaleString('fr-FR')} · La Conciergerie · ${fname(worker)}`, margin, pageH - 7)

  const safeName = (fname(worker) || 'salarie').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '_')
  doc.save(`modifications_pointage_${safeName}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.pdf`)
}

// ═══════════════════════════════════════════════════════════════════
// PDF RAPPORT DE MÉNAGE
// ═══════════════════════════════════════════════════════════════════
export const generateRapportPdf = async ({ rapport, bien, worker, struct, controle, hideWorker, dureeAnom }) => {
  if (!window.jspdf) { alert('Bibliothèque PDF non chargée. Rafraîchissez la page.'); return; }
  const { jsPDF } = window.jspdf
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageW = 210, pageH = 297, margin = 15
  const dateRapport = rapport.date ? new Date(rapport.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—'
  const dateSoumis = rapport.sub ? new Date(rapport.sub).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Non soumis (brouillon)'

  doc.setFillColor(45, 107, 62)
  doc.rect(0, 0, pageW, 28, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text('LA CONCIERGERIE', margin, 13)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.text('Rapport de fin de ménage', margin, 19)
  doc.text(`Document destiné au propriétaire — référence ${rapport.id}`, margin, 24)

  doc.setFillColor(255, 255, 255)
  doc.setDrawColor(255, 255, 255)
  doc.roundedRect(pageW - margin - 72, 5, 72, 18, 2, 2, 'F')
  doc.setTextColor(45, 107, 62)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text((bien?.name || '—').substring(0, 30), pageW - margin - 68, 11)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(80, 80, 80)
  doc.text((bien?.addr || '').substring(0, 40), pageW - margin - 68, 16)
  doc.text(dateRapport, pageW - margin - 68, 20)

  let y = 38

  // ─── Seuil qualité signalé (v158-2, 14/08/2026) ───
  // `dureeAnom` = entrée(s) dureeAnom7 liée(s) à ce rapport via `tid`, résolue par
  // l'appelant (jamais calculée ici). Double garde-fou non contournable : cette section
  // ne s'imprime QUE si `dureeAnom` est explicitement fourni ET que `hideWorker` n'est
  // pas actif — `generateRapportPdf` est partagée avec ClientUI.jsx (hideWorker:true,
  // dureeAnom jamais transmis côté client), donc l'info reste strictement interne même
  // si un futur appel oubliait l'un des deux garde-fous.
  if (dureeAnom && !hideWorker) {
    const listeDuree = Array.isArray(dureeAnom) ? dureeAnom : [dureeAnom]
    if (listeDuree.length) {
      // Une ligne de plus par entrée "trop rapide" où la qualité a été confirmée (v159, 14/08/2026)
      const lineCount = listeDuree.reduce((n, a) => n + ((a.sens === 'sous' && a.qualiteConfirmee) ? 2 : 1), 0)
      const boxH = 9 + lineCount * 6
      doc.setFillColor(255, 244, 214)
      doc.setDrawColor(224, 168, 0)
      doc.roundedRect(margin, y, pageW - margin * 2, boxH, 1.5, 1.5, 'FD')
      doc.setTextColor(122, 90, 0)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(9.5)
      doc.text('Seuil qualite signale', margin + 4, y + 6.5)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      let ly = y + 12
      listeDuree.forEach(a => {
        const lbl = a.sens === 'sous' ? 'Trop rapide' : 'Trop long'
        const statut = a.traite ? 'Traité' : 'En attente'
        doc.text(`${lbl} — ${a.motif || '—'} (${statut})`, margin + 4, ly)
        ly += 6
        if (a.sens === 'sous' && a.qualiteConfirmee) {
          doc.setFont('helvetica', 'italic')
          doc.text('Qualité confirmée malgré le temps réduit.', margin + 4, ly)
          doc.setFont('helvetica', 'normal')
          ly += 6
        }
      })
      y += boxH + 6
    }
  }

  // ─── Surlignage contrôle qualité v2 : bandeau résumé (v190, 08/09/2026) ───
  // Même garde que seuil qualité : jamais côté propriétaire (hideWorker). Le contrôle
  // est un document interne — si le PDF rapport est partagé avec le propriétaire
  // (ClientUI.jsx, hideWorker:true), ce bandeau n'apparaît jamais.
  const ctrlV2Actif = controle?.version === 2 && !hideWorker
  if (ctrlV2Actif) {
    const nc = controle.nonConformites || 0
    const totCtrl = controle.totalItemsRapport || 0
    const ctrlNs = Object.values(controle.pieces || {}).map(p => p?.note).filter(Boolean)
    const ctrlAvg = ctrlNs.length ? (ctrlNs.reduce((s, n) => s + n, 0) / ctrlNs.length).toFixed(1) : null
    const verdLb = { pret: 'Pret', nonpret: 'Non pret', reporter: 'Reporte' }[controle.verdict] || '—'
    const boxH = 14
    if (nc > 0) {
      doc.setFillColor(253, 224, 224); doc.setDrawColor(200, 60, 60)
    } else {
      doc.setFillColor(225, 247, 238); doc.setDrawColor(45, 140, 80)
    }
    doc.roundedRect(margin, y, pageW - margin * 2, boxH, 1.5, 1.5, 'FD')
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5)
    doc.setTextColor(nc > 0 ? 200 : 45, nc > 0 ? 60 : 140, nc > 0 ? 60 : 80)
    doc.text(`Controle qualite — ${nc > 0 ? nc + ' non-conformite(s)' : 'aucune non-conformite'}`, margin + 4, y + 6)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8)
    doc.text(`Verdict : ${verdLb}  |  ${nc} / ${totCtrl} items  ${ctrlAvg ? '|  Score ' + ctrlAvg + '/4' : ''}`, margin + 4, y + 11)
    y += boxH + 6
  }

  doc.setTextColor(45, 107, 62)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.text('Synthèse du ménage', margin, y)
  y += 2
  doc.setDrawColor(45, 107, 62)
  doc.setLineWidth(0.5)
  doc.line(margin, y, pageW - margin, y)
  y += 6

  const total = struct.items.length
  const valid = Object.entries(rapport.cks || {}).filter(([_, v]) => v).length
  const nbPhotoItems = struct.photoItems.length
  const nbPhotoItemsFournies = Object.entries(rapport.pk || {}).filter(([_, v]) => v).length
  const nbPhotosGen = struct.genPhotos.length
  const nbPhotosGenFournies = Object.entries(rapport.gp || {}).filter(([_, v]) => v).length
  const pct = total > 0 ? Math.round((valid / total) * 100) : 0

  doc.autoTable({
    startY: y,
    head: [hideWorker
      ? ['Soumis le', 'Points validés', 'Photos points', 'Photos pièces', 'Taux']
      : ['Salarié', 'Soumis le', 'Points validés', 'Photos points', 'Photos pièces', 'Taux']],
    body: [(hideWorker ? [] : [fname(worker) || '—']).concat([
      dateSoumis,
      `${valid} / ${total}`,
      `${nbPhotoItemsFournies} / ${nbPhotoItems}`,
      `${nbPhotosGenFournies} / ${nbPhotosGen}`,
      `${pct}%`,
    ])],
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 3, halign: 'center' },
    headStyles: { fillColor: [45, 107, 62], textColor: 255, fontStyle: 'bold', fontSize: 9 },
    margin: { left: margin, right: margin },
  })
  y = doc.lastAutoTable.finalY + 8

  doc.setTextColor(45, 107, 62)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.text('Détail des vérifications', margin, y)
  y += 2
  doc.line(margin, y, pageW - margin, y)
  y += 4

  struct.sections.forEach(sec => {
    if (y > pageH - 30) { doc.addPage(); y = 20 }
    doc.setTextColor(255, 134, 23)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.text(sec.title.replace(/[^\x00-\x7F]/g, ''), margin, y)
    y += 2
    // v190 : colonne Controle ajoutée quand un contrôle v2 est associé et visible (pas propriétaire)
    const headRow = ctrlV2Actif ? ['#', 'Point', 'Etat', 'Controle', 'Photo'] : ['#', 'Point', 'Etat', 'Photo']
    const bodyRows = sec.items.map((it, i) => {
      const checked = rapport.cks?.[it.id]
      const etat = checked ? 'OK' : 'NON COCHE'
      const photo = (it.photo || it.pk) ? (rapport.pk?.[it.id] ? 'Oui' : 'Manquante') : ''
      if (ctrlV2Actif) {
        const anomalie = checked && controle.items?.[it.id]?.ok === false
        const ctrlCol = anomalie ? 'ANOMALIE' : ''
        return [i + 1, it.lb || '—', etat, ctrlCol, photo]
      }
      return [i + 1, it.lb || '—', etat, photo]
    })
    const colStyles = ctrlV2Actif
      ? { 0: { cellWidth: 8, halign: 'center' }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 25, halign: 'center' }, 3: { cellWidth: 28, halign: 'center' }, 4: { cellWidth: 22, halign: 'center' } }
      : { 0: { cellWidth: 8, halign: 'center' }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 30, halign: 'center' }, 3: { cellWidth: 25, halign: 'center' } }
    const etatColIdx = 2
    const ctrlColIdx = ctrlV2Actif ? 3 : -1
    const photoColIdx = ctrlV2Actif ? 4 : 3
    doc.autoTable({
      startY: y,
      head: [headRow],
      body: bodyRows,
      theme: 'striped',
      styles: { fontSize: 8.5, cellPadding: 2 },
      headStyles: { fillColor: [230, 230, 230], textColor: 50, fontStyle: 'bold', fontSize: 8.5 },
      columnStyles: colStyles,
      didParseCell: cellData => {
        if (cellData.column.index === etatColIdx && cellData.section === 'body') {
          if (cellData.cell.raw === 'OK') { cellData.cell.styles.textColor = [45, 140, 80]; cellData.cell.styles.fontStyle = 'bold' }
          else if (cellData.cell.raw === 'NON COCHE') { cellData.cell.styles.textColor = [200, 60, 60]; cellData.cell.styles.fontStyle = 'bold' }
        }
        if (cellData.column.index === ctrlColIdx && cellData.section === 'body') {
          if (cellData.cell.raw === 'ANOMALIE') { cellData.cell.styles.textColor = [200, 60, 60]; cellData.cell.styles.fontStyle = 'bold'; cellData.cell.styles.fillColor = [253, 224, 224] }
        }
        if (cellData.column.index === photoColIdx && cellData.section === 'body') {
          if (cellData.cell.raw === 'Manquante') { cellData.cell.styles.textColor = [200, 140, 30]; cellData.cell.styles.fontStyle = 'bold' }
        }
      },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 5
  })

  // ─── Résumé anomalies contrôle v2 après le détail (v190) ───
  if (ctrlV2Actif) {
    const nbAnom = Object.values(controle.items || {}).filter(v => v.ok === false).length
    if (y > pageH - 20) { doc.addPage(); y = 20 }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9)
    if (nbAnom > 0) {
      doc.setTextColor(200, 60, 60)
      doc.text(`${nbAnom} anomalie(s) constatee(s) au controle qualite sur ${controle.totalItemsRapport || 0} items.`, margin, y)
      y += 5
      // Détail des anomalies avec observations du contrôleur
      Object.entries(controle.items || {}).forEach(([id, v]) => {
        if (v.ok !== false) return
        const found = struct.items.find(it => it.id === id)
        const lb = (found?.lb || found?.l || id).substring(0, 50)
        const obs = v.obs ? ` — ${v.obs}` : ''
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8)
        doc.setTextColor(200, 60, 60)
        if (y > pageH - 15) { doc.addPage(); y = 20 }
        doc.text(`  - ${lb}${obs}`.substring(0, 90), margin, y)
        y += 4
      })
    } else {
      doc.setTextColor(45, 140, 80)
      doc.text('Aucune anomalie constatee au controle qualite.', margin, y)
    }
    y += 6
  }

  const allPhotos = [
    ...struct.photoItems.map(it => ({ src: rapport.pk?.[it.id], label: it.lb, kind: 'point' })),
    ...struct.genPhotos.map(p => ({ src: rapport.gp?.[p.id], label: `${p._rmName || ''} - ${p.l}`.replace(/^- /, ''), kind: 'piece' })),
  ].filter(p => p.src)

  if (allPhotos.length > 0) {
    const resolved = await Promise.all(allPhotos.map(p => urlToDataUrlForPdf(p.src)))
    allPhotos.forEach((p, i) => { p.src = resolved[i] })
  }
  const allPhotosOk = allPhotos.filter(p => p.src)

  if (allPhotosOk.length > 0) {
    doc.addPage(); y = 20
    doc.setTextColor(45, 107, 62)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(14)
    doc.text(`Photos jointes (${allPhotosOk.length})`, margin, y)
    y += 2
    doc.setLineWidth(0.5)
    doc.line(margin, y, pageW - margin, y)
    y += 8

    const cols = 2
    const photoW = (pageW - margin * 2 - 8) / cols
    const photoH = photoW * 0.75
    let col = 0
    allPhotosOk.forEach(p => {
      if (y + photoH + 12 > pageH - 15) { doc.addPage(); y = 20; col = 0 }
      const x = margin + col * (photoW + 8)
      try { doc.addImage(p.src, 'JPEG', x, y, photoW, photoH, undefined, 'FAST') }
      catch (e) {
        doc.setFillColor(240, 240, 240); doc.rect(x, y, photoW, photoH, 'F')
        doc.setTextColor(150, 150, 150); doc.setFontSize(9)
        doc.text('Image non lisible', x + photoW / 2, y + photoH / 2, { align: 'center' })
      }
      doc.setTextColor(80, 80, 80); doc.setFont('helvetica', 'normal'); doc.setFontSize(8)
      doc.text((p.label || '').substring(0, 55), x, y + photoH + 5)
      doc.setFontSize(7); doc.setTextColor(150, 150, 150)
      doc.text(p.kind === 'point' ? 'Point de contrôle' : 'Photo pièce', x, y + photoH + 9)
      col++
      if (col >= cols) { col = 0; y += photoH + 12 }
    })
    // Dernière rangée incomplète (nombre de photos impair) : la position n'a pas été
    // avancée par le `if(col>=cols)` ci-dessus — sans ce flush, la section suivante se
    // dessine par-dessus la dernière photo (bug signalé par Claire, v151).
    if (col !== 0) { y += photoH + 12 }
  }

  const bfArr = Array.isArray(bien?.bf) ? bien.bf : []
  const bfActive = bien?.bfOn && bfArr.length > 0
  const lignesBill = bfActive ? bfArr.map(f => {
    const v = (rapport.bill || {})[f.id]
    const tu = Number(f.tu) || 0
    if (f.t === 'n') {
      const qty = Number(v) || 0
      return { lb: f.lb, em: f.em || '', qty, unite: 'unité', tu, montant: qty * tu, actif: qty > 0, type: 'n' }
    }
    const checked = v === true
    return { lb: f.lb, em: f.em || '', qty: checked ? 1 : 0, unite: '', tu, montant: checked ? tu : 0, actif: checked, type: 'b' }
  }).filter(l => l.actif) : []
  const totalBill = lignesBill.reduce((s, l) => s + l.montant, 0)
  const hasBill = lignesBill.length > 0
  const hasNotes = rapport.notes && rapport.notes.trim()

  if (hasBill || hasNotes) {
    if (y > pageH - 50) { doc.addPage(); y = 20 } else y += 10
    doc.setTextColor(45, 107, 62); doc.setFont('helvetica', 'bold'); doc.setFontSize(12)
    doc.text('Facturation & observations', margin, y)
    y += 2; doc.line(margin, y, pageW - margin, y); y += 6
    if (hasBill) {
      const fE = n => (Number(n) || 0).toFixed(2).replace('.', ',') + ' EUR'
      const body = lignesBill.map(l => [l.lb, l.type === 'n' ? String(l.qty) : 'Coche', l.type === 'n' ? fE(l.tu) + ' / u.' : fE(l.tu), fE(l.montant)])
      body.push([{ content: 'TOTAL EXTRAS', colSpan: 3, styles: { fontStyle: 'bold', halign: 'right', fillColor: [255, 244, 232] } }, { content: fE(totalBill), styles: { fontStyle: 'bold', fillColor: [255, 244, 232], textColor: [45, 140, 80] } }])
      doc.autoTable({
        startY: y,
        head: [['Element', 'Quantite', 'Tarif unitaire', 'Montant']],
        body,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 2.5 },
        headStyles: { fillColor: [45, 107, 62], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
        margin: { left: margin, right: margin },
      })
      y = doc.lastAutoTable.finalY + 6
    }
    if (hasNotes) {
      doc.setTextColor(80, 80, 80); doc.setFont('helvetica', 'normal'); doc.setFontSize(9)
      const lines = doc.splitTextToSize(rapport.notes, pageW - margin * 2)
      doc.text(lines, margin, y)
      y += lines.length * 4.2
    }
  }

  // ─── Engagement salarié (v159, 14/08/2026) ───
  // Contrairement à la section « Seuil qualité signalé » (interne uniquement), cette ligne
  // s'affiche sur TOUTES les versions du PDF, y compris côté client (ClientUI.jsx) — c'est
  // un signal de confiance pour le propriétaire, pas une information sensible. `rapport.engage`
  // n'existe que sur les rapports soumis depuis cette version ; absent = rien n'est affiché
  // (rétrocompatibilité totale, aucune migration).
  if (rapport.engage) {
    if (y > pageH - 25) { doc.addPage(); y = 20 }
    else y += 8
    doc.setDrawColor(210, 210, 210); doc.setLineWidth(0.2); doc.line(margin, y, pageW - margin, y)
    y += 6
    doc.setTextColor(45, 107, 62); doc.setFont('helvetica', 'italic'); doc.setFontSize(8.5)
    const dateEngage = rapport.engageTs ? new Date(rapport.engageTs).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''
    doc.text(`Rapport certifié sincère et exact par ${worker ? fname(worker) : 'le salarié'}${dateEngage ? ' le ' + dateEngage : ''}.`, margin, y)
  }

  const pageCount = doc.internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i); doc.setTextColor(150, 150, 150); doc.setFontSize(8)
    doc.text(`La Conciergerie · Colmar · Généré le ${new Date().toLocaleString('fr-FR')}`, margin, pageH - 8)
    doc.text(`Page ${i}/${pageCount}`, pageW - margin, pageH - 8, { align: 'right' })
  }

  const safeBien = (bien?.name || 'bien').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '_')
  doc.save(`rapport_menage_${safeBien}_${rapport.date || today()}.pdf`)
}

// ═══════════════════════════════════════════════════════════════════
// PDF CONTRÔLE D'APPARTEMENT
// ═══════════════════════════════════════════════════════════════════
export const generateControlePdf = async ({ controle, bien, rapport, worker, als, dureeAnom }) => {
  if (!window.jspdf) { alert('Bibliothèque PDF non chargée. Rafraîchissez la page.'); return; }
  const { jsPDF } = window.jspdf
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageW = 210, pageH = 297, margin = 15
  const dateCtrl = controle.closedAt ? new Date(controle.closedAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Non clôturé'
  const lastH = controle.history?.slice(-1)[0]
  const controleurNom = lastH?.by || '—'

  const VColors = {
    pret: { rgb: [45, 140, 80], lb: 'PRÊT' },
    nonpret: { rgb: [200, 60, 60], lb: 'NON PRÊT' },
    reporter: { rgb: [180, 130, 30], lb: 'REPORTÉ' },
    abandonne: { rgb: [120, 120, 120], lb: 'ABANDONNÉ' },
  }
  const verdCol = VColors[controle.verdict] || VColors.abandonne

  doc.setFillColor(45, 107, 62)
  doc.rect(0, 0, pageW, 28, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text('LA CONCIERGERIE', margin, 13)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.text("Rapport de contrôle d'appartement", margin, 19)
  doc.text(`Document interne — référence ${controle.id}`, margin, 24)

  doc.setFillColor(255, 255, 255)
  doc.setDrawColor(255, 255, 255)
  doc.roundedRect(pageW - margin - 72, 5, 72, 18, 2, 2, 'F')
  doc.setTextColor(45, 107, 62)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text((bien?.name || '—').substring(0, 30), pageW - margin - 68, 11)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(80, 80, 80)
  doc.text((bien?.addr || '').substring(0, 40), pageW - margin - 68, 16)
  doc.text(dateCtrl, pageW - margin - 68, 20)

  let y = 38

  const cIsV2 = controle.version === 2
  const struct = cIsV2 && bien ? getRapportStructure(bien) : null
  const dynSections = struct?.sections || []
  const piecesKeys = cIsV2 ? dynSections.map(s => s.key) : CTRL_PIECES.map(p => p.id)
  const notes = piecesKeys.map(k => controle.pieces?.[k]?.note).filter(Boolean)
  const avg = notes.length ? (notes.reduce((s, n) => s + n, 0) / notes.length).toFixed(1) : null
  // v190 : couleur du bandeau verdict basée sur le SCORE (pas le verdict)
  // Score >= 3 → vert (bon) | 2–2.9 → orange (passable) | < 2 → rouge (insuffisant) | pas de score → verdict
  const avgF = avg ? parseFloat(avg) : null
  const bandeauRgb = avgF !== null
    ? (avgF >= 3 ? [45, 140, 80] : avgF >= 2 ? [180, 130, 30] : [200, 60, 60])
    : verdCol.rgb
  doc.setFillColor(...bandeauRgb)
  doc.roundedRect(margin, y, pageW - margin * 2, 14, 2, 2, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(13)
  doc.text(`VERDICT : ${verdCol.lb}`, margin + 4, y + 9)
  if (avg) { doc.setFontSize(11); doc.text(`Score : ${avg} / 4`, pageW - margin - 4, y + 9, { align: 'right' }) }
  y += 20

  // ─── Seuil qualité signalé (v158-2, 14/08/2026) ───
  // Même bloc que generateRapportPdf, résolu par l'appelant via `tid` du rapport lié.
  // Pas de garde `hideWorker` ici : generateControlePdf n'a aucun appelant côté client
  // aujourd'hui (vérifié par grep sur ClientUI.jsx/GerantClientUI.jsx) — si un appel
  // client venait à être ajouté un jour, ce point devra être revu à ce moment-là.
  if (dureeAnom) {
    const listeDuree = Array.isArray(dureeAnom) ? dureeAnom : [dureeAnom]
    if (listeDuree.length) {
      const lineCount = listeDuree.reduce((n, a) => n + ((a.sens === 'sous' && a.qualiteConfirmee) ? 2 : 1), 0)
      const boxH = 9 + lineCount * 6
      doc.setFillColor(255, 244, 214)
      doc.setDrawColor(224, 168, 0)
      doc.roundedRect(margin, y, pageW - margin * 2, boxH, 1.5, 1.5, 'FD')
      doc.setTextColor(122, 90, 0)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(9.5)
      doc.text('Seuil qualite signale', margin + 4, y + 6.5)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      let ly = y + 12
      listeDuree.forEach(a => {
        const lbl = a.sens === 'sous' ? 'Trop rapide' : 'Trop long'
        const statut = a.traite ? 'Traité' : 'En attente'
        doc.text(`${lbl} — ${a.motif || '—'} (${statut})`, margin + 4, ly)
        ly += 6
        if (a.sens === 'sous' && a.qualiteConfirmee) {
          doc.setFont('helvetica', 'italic')
          doc.text('Qualité confirmée malgré le temps réduit.', margin + 4, ly)
          doc.setFont('helvetica', 'normal')
          ly += 6
        }
      })
      y += boxH + 6
    }
  }

  if (rapport) {
    doc.setTextColor(45, 107, 62); doc.setFont('helvetica', 'bold'); doc.setFontSize(12)
    doc.text('Ménage associé', margin, y)
    y += 2
    doc.setDrawColor(45, 107, 62); doc.setLineWidth(0.5); doc.line(margin, y, pageW - margin, y)
    y += 6

    const dateMenage = rapport.date ? new Date(rapport.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—'
    const dateSoumis = rapport.sub ? new Date(rapport.sub).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Non soumis (brouillon)'
    const { startTs, endTs, ms } = pointageTache(rapport.tid, als)
    const heureDebut = startTs ? new Date(startTs).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'
    const heureFin = endTs ? new Date(endTs).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'
    const duree = ms !== null ? fdur(ms) : '—'

    doc.autoTable({
      startY: y,
      head: [['Salarié', 'Date du ménage', 'Pointé de', 'à', 'Durée effectuée', 'Soumis le']],
      body: [[fname(worker) || '—', dateMenage, heureDebut, heureFin, duree, dateSoumis]],
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 3, halign: 'center' },
      headStyles: { fillColor: [45, 107, 62], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 8
  }

  doc.setTextColor(45, 107, 62); doc.setFont('helvetica', 'bold'); doc.setFontSize(12)
  doc.text('Synthèse du contrôle', margin, y)
  y += 2
  doc.setDrawColor(45, 107, 62); doc.setLineWidth(0.5); doc.line(margin, y, pageW - margin, y)
  y += 6

  doc.autoTable({
    startY: y,
    head: [['Contrôleur', 'Clôturé le', 'Pièces notées', 'Vérifications OK', 'Photos jointes']],
    body: [[
      controleurNom,
      dateCtrl,
      `${notes.length} / ${piecesKeys.length}`,
      `${Object.values(controle.checks || {}).filter(v => v === true).length} / ${CTRL_CHECKS.length}`,
      String(piecesKeys.reduce((s, k) => s + (controle.pieces?.[k]?.photos?.length || 0), 0)),
    ]],
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 3, halign: 'center' },
    headStyles: { fillColor: [45, 107, 62], textColor: 255, fontStyle: 'bold', fontSize: 9 },
    margin: { left: margin, right: margin },
  })
  y = doc.lastAutoTable.finalY + 8

  doc.setTextColor(45, 107, 62); doc.setFont('helvetica', 'bold'); doc.setFontSize(12)
  doc.text('Notation par pièce', margin, y)
  y += 2; doc.line(margin, y, pageW - margin, y); y += 4

  const noteLabels = { 1: 'Insuffisant', 2: 'Passable', 3: 'Bien', 4: 'Excellent' }
  const piecesTableBody = cIsV2
    ? dynSections.map(s => {
        const pd = controle.pieces?.[s.key]
        const label = (s.room?.nom || s.title || '').substring(0, 30)
        return [label, pd?.note ? String(pd.note) : '—', pd?.note ? (noteLabels[pd.note] || '—') : 'Non notee', pd?.obs || '']
      })
    : CTRL_PIECES.map(p => {
        const pd = controle.pieces?.[p.id]
        return [p.label, pd?.note ? String(pd.note) : '—', pd?.note ? (noteLabels[pd.note] || '—') : 'Non notee', pd?.obs || '']
      })
  doc.autoTable({
    startY: y,
    head: [['Piece', 'Note /4', 'Appreciation', 'Observations']],
    body: piecesTableBody,
    theme: 'striped',
    styles: { fontSize: 9, cellPadding: 2.5, valign: 'top' },
    headStyles: { fillColor: [230, 230, 230], textColor: 50, fontStyle: 'bold', fontSize: 9 },
    columnStyles: { 0: { cellWidth: 35, fontStyle: 'bold' }, 1: { cellWidth: 18, halign: 'center' }, 2: { cellWidth: 30 }, 3: { cellWidth: 'auto' } },
    didParseCell: cellData => {
      if (cellData.column.index === 1 && cellData.section === 'body') {
        const v = parseInt(cellData.cell.raw)
        // Même dégradé rouge→orange→vert que NoteEtoiles (SecRapports.jsx) — v151, cohérence app/PDF.
        if (v <= 1) { cellData.cell.styles.textColor = [232, 69, 69]; cellData.cell.styles.fontStyle = 'bold' }
        else if (v === 2) { cellData.cell.styles.textColor = [255, 134, 23]; cellData.cell.styles.fontStyle = 'bold' }
        else if (v === 3) { cellData.cell.styles.textColor = [134, 189, 146]; cellData.cell.styles.fontStyle = 'bold' }
        else if (v >= 4) { cellData.cell.styles.textColor = [58, 158, 95]; cellData.cell.styles.fontStyle = 'bold' }
      }
    },
    margin: { left: margin, right: margin },
  })
  y = doc.lastAutoTable.finalY + 8

  // ── v2 : section non-conformités rapport de ménage ──
  if (cIsV2 && Object.keys(controle.items || {}).length > 0) {
    if (y > pageH - 50) { doc.addPage(); y = 20 }
    doc.setTextColor(200, 60, 60); doc.setFont('helvetica', 'bold'); doc.setFontSize(12)
    doc.text('Non-conformites rapport de menage', margin, y)
    y += 2; doc.setDrawColor(200, 60, 60); doc.setLineWidth(0.5); doc.line(margin, y, pageW - margin, y); y += 4

    const allItems = struct?.items || []
    const ncBody = Object.entries(controle.items || {}).filter(([_, v]) => v.ok === false).map(([id, v]) => {
      const found = allItems.find(it => it.id === id)
      const lb = (found?.lb || found?.l || id).substring(0, 50)
      return [lb, v.obs || '', 'NON CONFORME']
    })
    doc.autoTable({
      startY: y,
      head: [['Item du rapport', 'Observation du controleur', 'Constat']],
      body: ncBody,
      theme: 'striped',
      styles: { fontSize: 9, cellPadding: 2.5, valign: 'top' },
      headStyles: { fillColor: [253, 224, 224], textColor: [200, 60, 60], fontStyle: 'bold', fontSize: 9 },
      columnStyles: { 0: { cellWidth: 50, fontStyle: 'bold' }, 2: { cellWidth: 32, halign: 'center', textColor: [200, 60, 60], fontStyle: 'bold' } },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 4

    const nc = controle.nonConformites || 0
    const tot = controle.totalItemsRapport || 0
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9)
    doc.setTextColor(200, 60, 60)
    doc.text(`Total : ${nc} non-conformite(s) sur ${tot} items du rapport`, margin, y + 4)
    y += 12
  } else if (cIsV2) {
    if (y > pageH - 30) { doc.addPage(); y = 20 }
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(45, 140, 80)
    doc.text('Non-conformites rapport de menage : aucune', margin, y)
    y += 8
  }

  if (y > pageH - 50) { doc.addPage(); y = 20 }
  doc.setTextColor(45, 107, 62); doc.setFont('helvetica', 'bold'); doc.setFontSize(12)
  doc.text('Vérifications de sortie', margin, y)
  y += 2; doc.line(margin, y, pageW - margin, y); y += 4

  doc.autoTable({
    startY: y,
    head: [['Point de contrôle', 'État']],
    body: CTRL_CHECKS.map(c => {
      const v = controle.checks?.[c.id]
      return [c.label, v === true ? 'OK' : v === false ? 'NON CONFORME' : 'Non vérifié']
    }),
    theme: 'striped',
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: [230, 230, 230], textColor: 50, fontStyle: 'bold', fontSize: 9 },
    columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 40, halign: 'center', fontStyle: 'bold' } },
    didParseCell: cellData => {
      if (cellData.column.index === 1 && cellData.section === 'body') {
        if (cellData.cell.raw === 'OK') cellData.cell.styles.textColor = [45, 140, 80]
        else if (cellData.cell.raw === 'NON CONFORME') cellData.cell.styles.textColor = [200, 60, 60]
        else cellData.cell.styles.textColor = [150, 150, 150]
      }
    },
    margin: { left: margin, right: margin },
  })
  y = doc.lastAutoTable.finalY + 8

  const photosCtrl = []
  if (cIsV2) {
    dynSections.forEach(s => {
      const phs = controle.pieces?.[s.key]?.photos || []
      const label = (s.room?.nom || s.title || '').substring(0, 30)
      phs.forEach((src, i) => photosCtrl.push({ src, label: `${label} - photo ${i + 1}` }))
    })
    // Photos d'items non-conformes
    Object.entries(controle.items || {}).forEach(([id, v]) => {
      (v.photos || []).forEach((src, i) => {
        const found = (struct?.items || []).find(it => it.id === id)
        const lb = (found?.lb || found?.l || id).substring(0, 30)
        photosCtrl.push({ src, label: `NC: ${lb} - photo ${i + 1}` })
      })
    })
  } else {
    CTRL_PIECES.forEach(p => {
      const phs = controle.pieces?.[p.id]?.photos || []
      phs.forEach((src, i) => photosCtrl.push({ src, label: `${p.label} - photo ${i + 1}` }))
    })
  }
  if (photosCtrl.length > 0) {
    const resolved = await Promise.all(photosCtrl.map(p => urlToDataUrlForPdf(p.src)))
    photosCtrl.forEach((p, i) => { p.src = resolved[i] })
  }
  const photosCtrlOk = photosCtrl.filter(p => p.src)

  if (photosCtrlOk.length > 0) {
    doc.addPage(); y = 20
    doc.setTextColor(45, 107, 62); doc.setFont('helvetica', 'bold'); doc.setFontSize(14)
    doc.text(`Photos du contrôle (${photosCtrlOk.length})`, margin, y)
    y += 2; doc.setLineWidth(0.5); doc.line(margin, y, pageW - margin, y); y += 8
    const cols = 2
    const photoW = (pageW - margin * 2 - 8) / cols
    const photoH = photoW * 0.75
    let col = 0
    photosCtrlOk.forEach(p => {
      if (y + photoH + 12 > pageH - 15) { doc.addPage(); y = 20; col = 0 }
      const x = margin + col * (photoW + 8)
      try { doc.addImage(p.src, 'JPEG', x, y, photoW, photoH, undefined, 'FAST') }
      catch (e) {
        doc.setFillColor(240, 240, 240); doc.rect(x, y, photoW, photoH, 'F')
        doc.setTextColor(150, 150, 150); doc.setFontSize(9)
        doc.text('Image non lisible', x + photoW / 2, y + photoH / 2, { align: 'center' })
      }
      doc.setTextColor(80, 80, 80); doc.setFont('helvetica', 'normal'); doc.setFontSize(8)
      doc.text((p.label || '').substring(0, 55), x, y + photoH + 5)
      col++
      if (col >= cols) { col = 0; y += photoH + 12 }
    })
    // Même flush que dans generateRapportPdf (v151) : une dernière rangée impaire
    // laissait `y` en retard, l'Historique/les Notes se dessinaient par-dessus la photo.
    if (col !== 0) { y += photoH + 12 }
  }

  if (controle.notes || (controle.history || []).length > 0) {
    if (y > pageH - 60) { doc.addPage(); y = 20 } else y += 4
    if (controle.notes) {
      doc.setTextColor(45, 107, 62); doc.setFont('helvetica', 'bold'); doc.setFontSize(12)
      doc.text('Notes du contrôleur', margin, y)
      y += 2; doc.line(margin, y, pageW - margin, y); y += 5
      doc.setTextColor(80, 80, 80); doc.setFont('helvetica', 'normal'); doc.setFontSize(9)
      const lines = doc.splitTextToSize(controle.notes, pageW - margin * 2)
      doc.text(lines, margin, y); y += lines.length * 4 + 6
    }
    if ((controle.history || []).length > 0) {
      if (y > pageH - 30) { doc.addPage(); y = 20 }
      doc.setTextColor(45, 107, 62); doc.setFont('helvetica', 'bold'); doc.setFontSize(12)
      doc.text('Historique des actions', margin, y)
      y += 2; doc.line(margin, y, pageW - margin, y); y += 4
      doc.autoTable({
        startY: y,
        head: [['Date & heure', 'Action', 'Par']],
        body: controle.history.map(h => {
          const actionLb = { pret: 'Validé prêt', nonpret: 'Validé non prêt', reporter: 'Reporté', abandonne: 'Abandonné' }[h.action] || h.action
          return [h.at ? new Date(h.at).toLocaleString('fr-FR') : '—', actionLb, h.by || '—']
        }),
        theme: 'striped',
        styles: { fontSize: 9, cellPadding: 2.5 },
        headStyles: { fillColor: [230, 230, 230], textColor: 50, fontStyle: 'bold', fontSize: 9 },
        margin: { left: margin, right: margin },
      })
    }
  }

  const pageCount = doc.internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i); doc.setTextColor(150, 150, 150); doc.setFontSize(8)
    doc.text(`La Conciergerie · Colmar · Généré le ${new Date().toLocaleString('fr-FR')}`, margin, pageH - 8)
    doc.text(`Page ${i}/${pageCount}`, pageW - margin, pageH - 8, { align: 'right' })
  }

  const safeBien = (bien?.name || 'bien').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '_')
  const dateStr = controle.closedAt ? new Date(controle.closedAt).toISOString().slice(0, 10) : today()
  doc.save(`controle_${safeBien}_${dateStr}.pdf`)
}
// ─── Génération PDF décompte mensuel des heures d'un salarié ───
// `contrat` (optionnel, Étape 1B) : le contrat couvert par CE décompte. Fourni
// par SecRH dès qu'un mois porte plusieurs contrats — Claire a tranché qu'on
// émet alors UN PDF PAR CONTRAT, chacun signé pour sa seule période, jamais un
// document unique qui mélangerait de l'avant et de l'après-solde de tout compte.
// Absent (cas d'un seul contrat sur le mois, et de tous les décomptes réédités
// pour le passé) : le cartouche retombe exactement sur l'ancien comportement.
export const generateDecomptePdf = ({ worker, year, month, rows, totaux, absences, leave, contrat }) => {
  if (!window.jspdf) { alert('Bibliothèque PDF non chargée. Rafraîchissez la page.'); return; }
  const { jsPDF } = window.jspdf
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageW = 210
  const margin = 15
  const GREEN = [45, 107, 62]
  const GREY = [120, 120, 120]
  const RED = [180, 40, 40]

  const MOIS_LONG = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre']
  const moisStr = MOIS_LONG[month]
  const moisCap = `${moisStr.charAt(0).toUpperCase()+moisStr.slice(1)} ${year}`
  // Décompte scindé par contrat : on précise la période réellement couverte,
  // sinon deux PDF du même mois seraient indiscernables une fois imprimés.
  const fdCourt = d => d ? new Date(d+'T00:00:00').toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit' }) : ''
  const bornesContrat = () => {
    if (!contrat) return ''
    const moisDeb = `${year}-${String(month+1).padStart(2,'0')}-01`
    const moisFin = `${year}-${String(month+1).padStart(2,'0')}-${String(new Date(year, month+1, 0).getDate()).padStart(2,'0')}`
    const deb = contrat.hire > moisDeb ? contrat.hire : moisDeb
    const fin = contrat.endDate && contrat.endDate < moisFin ? contrat.endDate : moisFin
    return ` — du ${fdCourt(deb)} au ${fdCourt(fin)}`
  }
  const periodeStr = moisCap + bornesContrat()

  const fh = h => `${Math.floor(Math.abs(h))}h${Math.round((Math.abs(h)%1)*60).toString().padStart(2,'0')}`
  const signStr = e => e > 0.05 ? `+${fh(e)}` : e < -0.05 ? `-${fh(e)}` : '0h00'

  // ── En-tête ──
  doc.setFillColor(...GREEN)
  doc.rect(0, 0, pageW, 30, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text('LA CONCIERGERIE', margin, 13)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.text('Décompte mensuel des heures de travail', margin, 19)
  doc.text('Art. L3171-4 Code du travail — Document contradictoire', margin, 25)

  // Cartouche salarié
  doc.setFillColor(255, 255, 255)
  doc.setDrawColor(255, 255, 255)
  doc.roundedRect(pageW - margin - 80, 4, 80, 22, 2, 2, 'F')
  doc.setTextColor(...GREEN)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.text(fname(worker) || '—', pageW - margin - 76, 12)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(80, 80, 80)
  doc.text(worker.role || (worker._gerant ? 'Direction' : 'Salarié'), pageW - margin - 76, 17)
  // Conditions du contrat couvert par ce décompte (Étape 1B). Sans `contrat`
  // fourni, on garde mot pour mot l'ancienne ligne lue sur la fiche.
  const cW = contrat || worker
  doc.text(`${cW.ct || 'CDI'} · ${cW.hw || '?'}h/semaine · ${cW.wd || 5}j/sem`, pageW - margin - 76, 22)

  let y = 38

  // ── Titre période ──
  doc.setTextColor(...GREEN)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(13)
  doc.text(`Récapitulatif — ${periodeStr}`, margin, y)
  y += 2
  doc.setDrawColor(...GREEN)
  doc.setLineWidth(0.5)
  doc.line(margin, y, pageW - margin, y)
  y += 7

  // ── Tableau semaines ──
  const tableBody = rows.map(r => [
    r.label,
    r.joursInfo || '',
    fh(r.hC),
    fh(r.hP),
    fh(r.hE),
    r.hAbs > 0.01 ? fh(r.hAbs) : '—',
    r.isFuture ? 'prévu' : signStr(r.ecart),
  ])
  // Ligne total
  const totalJours = rows.reduce((acc,r) => acc + (parseInt(r.joursInfo)||0), 0)
  tableBody.push([
    { content: 'TOTAL', styles: { fontStyle: 'bold' } },
    { content: String(totalJours), styles: { fontStyle: 'bold', halign: 'center' } },
    { content: fh(totaux.hC), styles: { fontStyle: 'bold' } },
    { content: fh(totaux.hP), styles: { fontStyle: 'bold' } },
    { content: fh(totaux.hE), styles: { fontStyle: 'bold', textColor: GREEN } },
    totaux.hAbs > 0.01 ? fh(totaux.hAbs) : '—',
    { content: signStr(totaux.ecart), styles: { fontStyle: 'bold', textColor: totaux.ecart < -0.05 ? RED : totaux.ecart > 0.05 ? GREEN : GREY } },
  ])

  doc.autoTable({
    startY: y,
    head: [['Semaine', 'Jours travaillés', 'H. contrat', 'H. planif.', 'H. effectives', 'Absences', 'Écart']],
    body: tableBody,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 3, halign: 'center' },
    headStyles: { fillColor: GREEN, textColor: 255, fontStyle: 'bold', fontSize: 9 },
    columnStyles: {
      0: { halign: 'left', cellWidth: 40 },
      1: { cellWidth: 20, halign: 'center' },
      4: { textColor: GREEN, fontStyle: 'bold' },
    },
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      if (data.row.index === tableBody.length - 1) {
        data.cell.styles.fillColor = [235, 245, 238]
      }
    },
  })
  y = doc.lastAutoTable.finalY + 8

  // ── Synthèse ──
  doc.setFillColor(245, 250, 246)
  doc.setDrawColor(...GREEN)
  doc.setLineWidth(0.3)
  doc.roundedRect(margin, y, pageW - margin * 2, 22, 2, 2, 'FD')
  doc.setTextColor(60, 60, 60)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  const synth = [
    `Heures contractuelles : ${fh(totaux.hC)}`,
    `Heures effectivement travaillées : ${fh(totaux.hE)}`,
    `Heures d'absence : ${totaux.hAbs > 0.01 ? fh(totaux.hAbs) : '0h00'}`,
    `Écart mensuel : ${signStr(totaux.ecart)}${totaux.ecart < -0.05 ? ' (en dessous du contrat)' : totaux.ecart > 0.05 ? ' (heures complémentaires)' : ' (équilibré)'}`,
  ]
  synth.forEach((s, i) => {
    doc.text(s, margin + 6, y + 7 + i * 4.2)
  })
  y += 28

  // ── Absences du mois ──
  const absApproved = (leave || []).filter(l => {
    if (l.sid !== worker.id) return false
    if (l.st !== 'approved') return false
    const mFirst = `${year}-${String(month+1).padStart(2,'0')}-01`
    const mLast = new Date(year, month+1, 0).toISOString().slice(0,10)
    const sd = l.sd || ''
    const ed = l.ed || l.sd || ''
    return sd <= mLast && ed >= mFirst
  })

  if (absApproved.length > 0) {
    doc.setTextColor(...GREEN)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.text('Absences du mois', margin, y)
    y += 2
    doc.line(margin, y, pageW - margin, y)
    y += 4
    const TYPE_LABELS = { conge: 'Congés payés', demi: 'Demi-journée', recup: 'Récupération', repos: 'Repos hebdo', indispo: 'Indisponibilité', maladie: 'Arrêt maladie' }
    doc.autoTable({
      startY: y,
      head: [['Type', 'Du', 'Au (inclus)', 'Motif']],
      body: absApproved.map(l => [
        TYPE_LABELS[l.type] || l.type,
        l.sd ? new Date(l.sd+'T12:00:00').toLocaleDateString('fr-FR') : '—',
        l.ed ? new Date(l.ed+'T12:00:00').toLocaleDateString('fr-FR') : (l.sd ? new Date(l.sd+'T12:00:00').toLocaleDateString('fr-FR') : '—'),
        l.reason || '—',
      ]),
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2.5 },
      headStyles: { fillColor: [180, 120, 0], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 10
  } else {
    y += 2
  }

  // ── Zone signature ──
  // Vérifier si on a la place, sinon nouvelle page
  if (y > 230) { doc.addPage(); y = 20 }

  doc.setFillColor(250, 250, 248)
  doc.setDrawColor(180, 180, 180)
  doc.setLineWidth(0.3)
  doc.roundedRect(margin, y, pageW - margin * 2, 52, 2, 2, 'FD')

  doc.setTextColor(60, 60, 60)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.text('Validation du décompte', margin + 6, y + 7)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(100, 100, 100)
  doc.text('Ce document récapitule les heures de travail du mois. Le salarié signe et date ce document. À compter de la date de', margin + 6, y + 13)
  doc.text('signature, il dispose de 8 jours pour formuler une contestation écrite. Le décompte est établi sur la période mensuelle.', margin + 6, y + 18)

  // Deux colonnes signature
  const colW = (pageW - margin * 2 - 12) / 2
  // Employeur
  doc.setTextColor(60, 60, 60)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.text('Signature employeur', margin + 6, y + 27)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(120, 120, 120)
  doc.text('Lu et approuvé — La Conciergerie', margin + 6, y + 32)
  doc.setDrawColor(180, 180, 180)
  doc.line(margin + 6, y + 46, margin + 6 + colW - 6, y + 46)
  doc.setFontSize(7)
  doc.text('Date et signature', margin + 6, y + 50)

  // Salarié
  const col2X = margin + 6 + colW + 6
  doc.setTextColor(60, 60, 60)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.text(`Signature salarié — ${fname(worker) || ''}`, col2X, y + 27)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(120, 120, 120)
  doc.text('"Lu et approuvé — Bon pour accord"', col2X, y + 32)
  doc.line(col2X, y + 46, col2X + colW - 6, y + 46)
  doc.setFontSize(7)
  doc.text('Date et signature', col2X, y + 50)

  y += 56

  // ── Pied de page ──
  const pageCount = doc.internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    const pageH = doc.internal.pageSize.getHeight()
    doc.setFillColor(...GREEN)
    doc.rect(0, pageH - 12, pageW, 12, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    doc.text(`La Conciergerie · Colmar · Décompte ${periodeStr} · Généré le ${new Date().toLocaleString('fr-FR')}`, margin, pageH - 5)
    doc.text(`Page ${i}/${pageCount}`, pageW - margin, pageH - 5, { align: 'right' })
  }

  const safeName = (fname(worker) || 'salarie').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '_')
  doc.save(`decompte_heures_${safeName}_${year}_${String(month+1).padStart(2,'0')}.pdf`)
}

